import React, { useState, useEffect } from 'react';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { formatDate, formatDateForString } from 'utils/formatter';
import { GetUserResut, getUser, getUserTags, updateUser } from 'config/api';
import Loading from 'components/commons/loading/Loading';
import { TagListModal } from 'components/features/persona-chart/TagListModal';
import { PersonaChartAssetDistribution } from 'components/features/persona-chart/PersonaChartAssetDistribution';
import { PersonaChartAssetTransition } from 'components/features/persona-chart/PersonaChartAssetTransition';
import { PersonaRegistInfo } from 'components/features/persona-chart/PersonaRegistInfo';
import { PersonaRegistModal } from 'components/features/persona-chart/PersonaRegistModal';
import {
  UserTag,
  getInfo,
  getInfoFrom,
  getInfoTo,
  getUnit,
  investmentProductsList,
} from 'components/features/utils/personaChart';
import { getUserStorage } from 'components/features/utils/utils';
import { isIosAppNotPremium } from 'components/features/utils/userStatus';

export type UserTags = {
  ageTag: UserTag;
  annualIncomeTag: UserTag;
  assetTag: UserTag;
  investingYearsTag: UserTag;
  queryTags: UserTag[];
};

const PersonaChartContent = () => {
  const routing = useRouting();
  const [userInfo, setUserInfo] = useState<GetUserResut | null>(null);
  const [userTags, setUserTags] = useState<UserTags | null>(null);
  const [isHideUserInfo, setIsHideUserInfo] = useState<boolean | null>(null);

  useEffect(() => {
    getUser().then(ret => {
      setUserInfo(ret);
      setIsHideUserInfo(ret.HideAssetP);
    });

    getUserTags().then(ret => {
      const result = {
        ageTag: ret.AgeTag,
        annualIncomeTag: ret.AnnualIncomeTag,
        assetTag: ret.AssetTag,
        investingYearsTag: ret.InvestingYearsTag,
        queryTags: ret.QueryTags,
      } as UserTags;
      setUserTags(result);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    isShowTagListModal,
    { hideModal: hideTagListModal, showModal: showTagListModal },
  ] = useModal();

  const [
    isShowRegistModal,
    { hideModal: hideRegistModal, showModal: showRegistModal },
  ] = useModal();

  const user = getUserStorage();

  if (!user || userInfo === null || userTags === null) {
    return <Loading loading style={{ marginTop: '3rem' }} />;
  }

  if (userInfo.Birthday === null || userInfo.InvestmentStartDate === null) {
    return <PersonaRegistInfo userInfo={userInfo} />;
  }

  const thisYear = new Date().getFullYear();
  const startYear = Number(
    formatDateForString(userInfo.InvestmentStartDate, 'yyyy')
  );
  const investmentStartYear = thisYear - startYear;

  const updateHideAsset = async () => {
    setIsHideUserInfo(!isHideUserInfo);

    if (isHideUserInfo === null) return;
    await updateUser({
      HideAssetP: !isHideUserInfo,
    });
  };

  const getUserInfo = () => {
    getUser().then(ret => {
      setUserInfo(ret);
      setIsHideUserInfo(ret.HideAssetP);
    });

    getUserTags().then(ret => {
      const result = {
        ageTag: ret.AgeTag,
        annualIncomeTag: ret.AnnualIncomeTag,
        assetTag: ret.AssetTag,
        investingYearsTag: ret.InvestingYearsTag,
        queryTags: ret.QueryTags,
      } as UserTags;
      setUserTags(result);
    });
  };

  const thisDate = new Date();
  const dispDate =
    thisDate.getHours() === 0 && thisDate.getMinutes() < 30
      ? new Date(thisDate.setDate(thisDate.getDate() - 1))
      : thisDate;

  return (
    <>
      <div className="flex flex-col items-center p-4 bg-slate-800">
        <div className="w-full" style={{ maxWidth: '474px' }}>
          <div className="p-4 rounded-t-lg text-white bg-bluegray-700">
            <div className="flex flex-row items-center">
              {user.ProfileImageURL ? (
                <img
                  src={user.ProfileImageURL}
                  className="rounded-full w-10 h-10 mr-1"
                  alt="プロフィール"
                />
              ) : (
                <div
                  className="icon-person text-4xl border rounded-full border-gray-700 mr-1 pb-1 flex justify-center items-center"
                  style={{ width: '45px', height: '45px' }}></div>
              )}
              <div className="ml-1 text-lg font-bold">{user.Name}</div>
            </div>
          </div>
          <div className="flex flex-row pt-2 pb-1 px-4 text-xs flex-wrap bg-bluegray-600">
            {userTags.queryTags.length === 0 ? (
              <div className="mb-1">該当タグなし</div>
            ) : (
              userTags.queryTags.map((userTag, key) => {
                const item = investmentProductsList.find(
                  list => list.tag === userTag
                );
                const color =
                  item !== undefined
                    ? item.color
                    : 'bg-bluegray-100 border-bluegray-300';
                return (
                  <div
                    className={`py-1 px-2 mb-1 mr-1 border ${color} rounded-md`}
                    key={key}>
                    {item?.label}
                  </div>
                );
              })
            )}
          </div>
          {!isHideUserInfo && (
            <div className="pt-4 pb-4 px-4 bg-bluegray-700">
              <div className="flex flex-row">
                <div
                  className="grid items-center pr-2 text-xs text-white bg-bluegray-700 border-r border-bluegray-500"
                  style={{
                    gridTemplateColumns: 'auto auto auto',
                    width: '39%',
                  }}>
                  <div
                    className="text-right"
                    style={{ gridColumn: '1', gridRow: '1' }}>
                    年代
                  </div>
                  <div
                    className="ml-2 text-sm text-right font-bold"
                    style={{ gridColumn: '2', gridRow: '1' }}>
                    {getInfo(userTags.ageTag)}
                  </div>
                  <div
                    className="ml-1 text-left"
                    style={{ gridColumn: '3', gridRow: '1' }}>
                    {getUnit(userTags.ageTag)}
                  </div>
                  <div
                    className="text-right"
                    style={{ gridColumn: '1', gridRow: '2' }}>
                    投資歴
                  </div>
                  <div
                    className="ml-2 text-sm text-right font-bold"
                    style={{ gridColumn: '2', gridRow: '2' }}>
                    {investmentStartYear !== 0 ? investmentStartYear : '1'}
                  </div>
                  <div
                    className="ml-1 text-left"
                    style={{ gridColumn: '3', gridRow: '2' }}>
                    {investmentStartYear !== 0 ? '年' : '年未満'}
                  </div>
                </div>
                <div
                  className="grid items-center ml-2 text-xs text-white bg-bluegray-700"
                  style={{
                    gridTemplateColumns: 'auto auto auto',
                    width: '61%',
                  }}>
                  <div
                    className="text-right"
                    style={{ gridColumn: '1', gridRow: '1' }}>
                    資産総額
                  </div>
                  <div
                    className="ml-2 text-sm text-right font-bold"
                    style={{ gridColumn: '2', gridRow: '1' }}>
                    <span>{getInfoFrom(userTags.assetTag)}</span>
                    <span className="inline-block">
                      {getInfoTo(userTags.assetTag)}
                    </span>
                  </div>
                  <div
                    className="ml-1 text-left"
                    style={{ gridColumn: '3', gridRow: '1' }}>
                    {getUnit(userTags.assetTag)}
                  </div>
                  <div
                    className="text-right"
                    style={{ gridColumn: '1', gridRow: '2' }}>
                    年収
                  </div>
                  <div
                    className="ml-2 text-sm text-right font-bold"
                    style={{ gridColumn: '2', gridRow: '2' }}>
                    <span>{getInfoFrom(userTags.annualIncomeTag)}</span>
                    <span className="inline-block">
                      {getInfoTo(userTags.annualIncomeTag)}
                    </span>
                  </div>
                  <div
                    className="ml-1 text-left"
                    style={{ gridColumn: '3', gridRow: '2' }}>
                    {getUnit(userTags.annualIncomeTag)}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            className="flex flex-row items-center justify-center py-2 text-bluegray-500 bg-bluegray-700 border-t border-bluegray-500 rounded-b-lg cursor-pointer"
            onClick={() => updateHideAsset()}>
            <div
              className={`icon-${
                isHideUserInfo ? 'chevron-down' : 'chevron-up'
              } text-lg`}></div>
            <div className="ml-1 text-xs ">
              {isHideUserInfo ? '表示' : '非表示'}
            </div>
          </div>
          <div className="flex flex-row mt-2 text-xs text-white">
            <div className="cursor-pointer" onClick={showRegistModal}>
              <span className="icon-edit"></span>
              <span className="ml-1">あなたの情報を編集</span>
            </div>
            <div
              className="flex flex-row ml-auto cursor-pointer"
              onClick={showTagListModal}>
              <div>タグの表示条件</div>
              <div className="icon-help_outline ml-1 text-sm"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white px-4">
        <PersonaChartAssetDistribution
          {...{
            userTags,
            user,
            type: 'distribution',
          }}
        />
        <PersonaChartAssetTransition
          {...{
            userTags,
            user,
            type: 'transition',
          }}
        />

        {/* TODO: MAPのみ */}
        {!isIosAppNotPremium() && (
          <>
            <div className="pt-8">
              <span className="py-2 font-bold">おすすめメニュー</span>
              <div
                onClick={(): void =>
                  routing.pushRouting({ path: `/target` }, true)
                }>
                <div
                  className="grid px-2 mt-2 rounded-lg bg-teal-350 cursor-pointer"
                  style={{ gridTemplateColumns: 'auto 1fr' }}>
                  <div className="flex items-center">
                    <img
                      src="/static/images/persona/to_target.png"
                      alt="アイコン"
                      className="h-20 w-20"
                    />
                  </div>
                  <div className="my-auto ml-2 text-sm text-white">
                    <div className="text-sm">
                      資産目標サポート&達成へのサポート
                    </div>
                    <div className="text-base font-bold">資産目標サポート</div>
                  </div>
                </div>
              </div>
              <a
                href="https://minkabu.jp/stock/search?utm_source=asepla&utm_medium=pelsona&utm_campaign=botttom20231030"
                target="_blank"
                rel="noopener noreferrer">
                <div
                  className="grid px-2 mt-2 rounded-lg bg-lock"
                  style={{ gridTemplateColumns: 'auto 1fr' }}>
                  <div className="flex items-center">
                    <img
                      src="/static/images/persona/to_stockSearch.png"
                      alt="アイコン"
                      className="h-20 w-20"
                    />
                  </div>
                  <div className="my-auto ml-2 text-white">
                    <div className="text-sm">おすすめの銘柄を探しに行く</div>
                    <div className="text-base font-bold">
                      みんかぶおすすめ銘柄
                    </div>
                  </div>
                </div>
              </a>
            </div>
          </>
        )}

        <div className="pt-6 pb-4 text-xs text-right text-bluegray-500">
          {formatDate(dispDate, 'yyyy/MM/dd')}{' '}
          00:30の資産データを元に集計しています。
          <br />
          ※土日のデータは集計されません。
        </div>
      </div>

      <TagListModal hide={hideTagListModal} isShow={isShowTagListModal} />
      <PersonaRegistModal
        hide={hideRegistModal}
        isShow={isShowRegistModal}
        registBirthday={userInfo.Birthday}
        registAnnualIncome={userInfo.AnnualIncome}
        registInvestmentStartDate={userInfo.InvestmentStartDate}
        getUserInfo={getUserInfo}
        buttonText="完了"
        isFirst={false}
        close={hideRegistModal}
      />
    </>
  );
};

const PersonaChart = (): React.ReactElement => {
  // const routing = useRouting();
  return (
    <>
      {/* MEMO: 有料会員のみの機能に移行した際はコメントアウト外す */}
      {/* {isPremium() ? (
        <PersonaChartContent />
      ) : (
        <PremiumModal
          layoutPattern={5}
          onClose={() => routing.pushRouting({ path: '/home' }, true)}
        />
      )} */}
      <PersonaChartContent />
    </>
  );
};

export default PersonaChart;
